import type { LinkGroup, LinkTarget } from "@helm/core";
import type { OverlayOptions } from "@helm/chart";
import type { IconName } from "../components/Icon";
import type { DomControlVisibility } from "../widgets/OrderPad";

/** Widget catalogue ids (SPEC §17.3). Components are registered in panels.tsx. */
export type WidgetKind =
  | "chart"
  | "dom"
  | "execution"
  | "scalp-pad"
  | "ticket"
  | "tape"
  | "details"
  | "orderbook"
  | "positions"
  | "orders"
  | "fills"
  | "accounts"
  | "notifications"
  | "risk"
  | "copy"
  | "watchlist"
  | "screener"
  | "journal"
  | "alerts"
  | "notes"
  | "replay"
  | "strategies";

/** Order pad mode, saved per panel with the layout (SPEC §9.2: "saved per workspace"). */
export type PadMode = "scalp" | "dom" | "order";

export type WidgetParams = {
  kind: WidgetKind;
  link: LinkTarget;
  title?: string;
  /**
   * Chart timeframe, canonical string (`@helm/core/bars` timeframe.ts: "1m", "4h", "1D", "1W",
   * "1mo", "500t", "8r", "1000v", "4rk"); persisted with the layout. Old values keep working.
   */
  tf?: string;
  /** Chart range bars: phantom (gap-filler) bars; absent = on (Q-T5). Persisted with the layout. */
  rangePhantoms?: boolean;
  /**
   * Split chart (`@helm/core` chartLayout.ts): layout, pane specs, splitter ratios, sync flags.
   * Absent = a plain chart (layout "1" from `tf` / `rangePhantoms`); `tf` / `rangePhantoms` then
   * mirror the active pane.
   */
  chartLayout?: unknown;
  /**
   * Legacy (pre-split builds of the indicator framework, ADR-0018): the chart's indicators + panes
   * (`ChartIndicatorsState`). Read once into pane 0 of the chart layout (`readChartLayout`) and
   * deleted on the next chart write; indicators now live per pane in `chartLayout.panes[i]`.
   */
  indicators?: unknown;
  /**
   * Template seed (ADR-0026): chart settings (`ChartSettings`, style / scale / appearance) for pane
   * 0 of the chart layout; read once like `indicators` and deleted on the next chart write.
   */
  chartSettings?: unknown;
  /** Order pad mode for execution / scalp-pad / ticket panels. */
  mode?: PadMode;
  /**
   * Positions / Orders / Fills account filter (`AccountFilter`: "global" follows the top bar,
   * "all", "main", "account:<id>"). Missing in layouts saved before it existed = "global".
   */
  account?: string;
  /** Accounts module: "main" account only or "all" accounts (default). */
  accountsView?: string;
  /**
   * Positions / Accounts / Orders / Fills column state per table id (`ColumnState`: visible
   * columns in order, widths, sort, last template). Missing = the table's defaults; read through
   * `sanitizeColumnState`, so layouts saved before it existed keep working.
   */
  tables?: Record<string, unknown>;
  /**
   * Legacy / template seed (ADR-0019): chart overlays (heatmap, key levels, stats box,
   * position/order lines; missing keys take `DEFAULT_OVERLAYS`). Read into pane 0 of the chart
   * layout (`readChartLayout`) and deleted on the next chart write; overlays live per pane in
   * `chartLayout.panes[i].overlays`.
   */
  overlays?: Partial<OverlayOptions>;
  /** Chart module: show the drawing-tools rail at its left edge (Glacis layout; per module). */
  rail?: boolean;
  /**
   * The module's own symbol while it is **unlinked** (DOM, order pad, tape; owner 2026-09-28
   * "the DOM doesn't let me change the ticker"): a root (front month) or a contract, persisted with
   * the layout. A linked module shows its link colour's symbol instead (picking one retargets the
   * group, so a linked chart follows). Missing = none yet.
   */
  symbol?: string;
  /**
   * Chart module tabs (`@helm/core` chartTabs.ts, Tradovate): the other symbol + timeframe tabs with
   * their chart snapshots; the active tab is the ordinary chart fields. Missing = one tab.
   */
  chartTabs?: unknown;
  /** Chart module: the instrument header (contract details + order bar slot); missing = on in Tradovate style. */
  instrumentHeader?: boolean;
  /** Presentation only; emergency controls remain visible when chart entries are collapsed. */
  chartControlsCollapsed?: boolean;
  /** Presentation only; hides the optional DOM order-pad join row. */
  domJoinCollapsed?: boolean;
  domControls?: DomControlVisibility;
};

/**
 * The add-module menu (owner 2026-09-27, Tradovate "+ Add Modules"): every module type with its
 * icon and group. `single` modules are focused instead of added twice; `later` ones still open,
 * showing that they arrive in a later phase.
 */
export type ModuleInfo = {
  kind: WidgetKind;
  icon: IconName;
  group: "Trading" | "Market data" | "Account" | "Tools";
  hint: string;
  single?: boolean;
  later?: boolean;
};

export const MODULES: readonly ModuleInfo[] = [
  { kind: "chart", icon: "chart", group: "Trading", hint: "Candles, tick/range/volume/Renko bars" },
  { kind: "dom", icon: "ladder", group: "Trading", hint: "Price ladder with est. P/L" },
  { kind: "execution", icon: "orderPad", group: "Trading", hint: "Order panel: Scalp · DOM · Order" },
  { kind: "scalp-pad", icon: "bolt", group: "Trading", hint: "Compact Buy / Sell / Join pad" },
  { kind: "ticket", icon: "orderPad", group: "Trading", hint: "Market / Limit / Stop ticket" },
  { kind: "tape", icon: "tape", group: "Market data", hint: "Time & Sales" },
  { kind: "watchlist", icon: "list", group: "Market data", hint: "Symbols, last, change" },
  { kind: "screener", icon: "screener", group: "Market data", hint: "Futures gainers / losers / movers" },
  {
    kind: "details",
    icon: "dataBox",
    group: "Market data",
    hint: "Symbol details: contract, ticks, session",
  },
  { kind: "orderbook", icon: "book", group: "Market data", hint: "Depth by price", later: true },
  { kind: "accounts", icon: "accounts", group: "Account", hint: "Balance, P&L per account" },
  { kind: "positions", icon: "positions", group: "Account", hint: "Open positions, working orders" },
  { kind: "orders", icon: "orders", group: "Account", hint: "Order history and status" },
  { kind: "fills", icon: "fills", group: "Account", hint: "Executions" },
  { kind: "risk", icon: "shield", group: "Account", hint: "Firm rule tracker" },
  { kind: "notifications", icon: "bell", group: "Account", hint: "Order, position and risk events" },
  { kind: "journal", icon: "journal", group: "Tools", hint: "Daily Trading plan and grade", single: true },
  { kind: "notes", icon: "notes", group: "Tools", hint: "Free notes", later: true },
  { kind: "alerts", icon: "bell", group: "Tools", hint: "Price alerts: log, rules, watchers" },
  { kind: "replay", icon: "replay", group: "Tools", hint: "Replay transport", later: true },
  { kind: "strategies", icon: "code", group: "Tools", hint: "Strategy instances", later: true },
  { kind: "copy", icon: "copy", group: "Tools", hint: "Leader → followers copy trader", single: true },
];

export const MODULE_BY_KIND: ReadonlyMap<WidgetKind, ModuleInfo> = new Map(MODULES.map((m) => [m.kind, m]));

/** Account-level tables: a new one joins the existing table group as a tab (Tradesea bottom dock). */
export const TABLE_KINDS: ReadonlySet<WidgetKind> = new Set([
  "positions",
  "orders",
  "fills",
  "accounts",
  "notifications",
]);

export const WIDGET_TITLES: Record<WidgetKind, string> = {
  chart: "Chart",
  dom: "Ladder",
  execution: "Order",
  "scalp-pad": "Scalp",
  ticket: "Order ticket",
  tape: "Time & Sales",
  details: "Symbol details",
  orderbook: "Order book",
  positions: "Positions",
  orders: "Orders",
  fills: "Fills",
  accounts: "Accounts",
  notifications: "Notifications",
  risk: "Risk tracker",
  copy: "Copy trader",
  watchlist: "Watchlist",
  screener: "Futures screener",
  journal: "Daily Trading",
  alerts: "Alerts",
  notes: "Notes",
  replay: "Replay",
  strategies: "Strategies",
};

/** Default order-pad mode per kind. */
export const DEFAULT_PAD_MODE: Partial<Record<WidgetKind, PadMode>> = {
  execution: "dom",
  "scalp-pad": "scalp",
  ticket: "order",
};

/**
 * Default workspace templates (SPEC §17.3; look per owner mockups 01/03). Each entry is a list of
 * panels with a placement relative to an earlier panel; the Workspace component applies them
 * through the dockview API.
 */
export type TemplatePanel = {
  id: string;
  kind: WidgetKind;
  link: LinkTarget;
  position?: { referencePanel: string; direction: "left" | "right" | "above" | "below" | "within" };
  /** Width (left/right) or height (above/below) in px; on the anchor (first panel): its width. */
  size?: number;
  mode?: PadMode;
  /** Extra panel params (a chart grid's `chartLayout`). */
  params?: Partial<WidgetParams>;
  /** Chart timeframe (canonical string). */
  tf?: string;
  /** Chart overlays. */
  overlays?: Partial<OverlayOptions>;
  /** Chart drawing-tools rail. */
  rail?: boolean;
  /**
   * false: `size` only seeds the split and is not re-applied afterwards — for a panel whose cell
   * is later split again (its size is the column's, not its own).
   */
  pin?: boolean;
};

export type WorkspaceTemplate = {
  id: string;
  name: string;
  panels: TemplatePanel[];
  /**
   * Symbols the template's link groups start on when the group has none yet (a multi-symbol
   * layout needs a second group; a symbol the user already picked is never overwritten).
   */
  linkSymbols?: Partial<Record<LinkGroup, string>>;
};

/** Every overlay on (Glacis templates). */
const ALL_OVERLAYS: OverlayOptions = {
  heatmap: true,
  levels: true,
  stats: true,
  trades: true,
  orderBar: true,
};

/**
 * Default table group (Tradesea's bottom dock look): Positions, Orders, Fills, Accounts,
 * Notifications as ordinary panels tabbed under `ref`. Each tab can be dragged anywhere, split
 * off, floated, popped out or closed like any other module.
 */
function bottomDock(ref: string, size = 200): TemplatePanel[] {
  return [
    {
      id: "positions-1",
      kind: "positions",
      link: "unlinked",
      position: { referencePanel: ref, direction: "below" },
      size,
    },
    {
      id: "orders-1",
      kind: "orders",
      link: "unlinked",
      position: { referencePanel: "positions-1", direction: "within" },
    },
    {
      id: "fills-1",
      kind: "fills",
      link: "unlinked",
      position: { referencePanel: "positions-1", direction: "within" },
    },
    {
      id: "accounts-1",
      kind: "accounts",
      link: "unlinked",
      position: { referencePanel: "positions-1", direction: "within" },
    },
    {
      id: "notifications-1",
      kind: "notifications",
      link: "unlinked",
      position: { referencePanel: "positions-1", direction: "within" },
    },
  ];
}

export const TEMPLATES: WorkspaceTemplate[] = [
  {
    // Owner 2026-09-29 (Tradovate reference 9; the default template since then): DOM on the left,
    // two charts side by side — the first with symbol + timeframe tabs (NQ / GC / YM / MNQ 1m), the
    // second NQ 5m on its own link colour — and Accounts / Orders / Positions tabbed on the right.
    // Charts show the instrument header, quick buttons and the compact tool strip in Tradovate style.
    id: "tradovate",
    name: "Tradovate",
    linkSymbols: { red: "NQ", green: "NQ" },
    panels: [
      {
        id: "chart-1",
        kind: "chart",
        link: "red",
        tf: "1m",
        params: {
          chartTabs: {
            tabs: [
              { id: "t1", symbol: "NQ" },
              { id: "t2", symbol: "GC", chart: { tf: "1m" } },
              { id: "t3", symbol: "YM", chart: { tf: "1m" } },
              { id: "t4", symbol: "MNQ", chart: { tf: "1m" } },
            ],
            active: 0,
          },
        },
      },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "left" },
        size: 240,
      },
      {
        id: "accounts-1",
        kind: "accounts",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 360,
      },
      {
        id: "orders-1",
        kind: "orders",
        link: "unlinked",
        position: { referencePanel: "accounts-1", direction: "within" },
      },
      {
        id: "positions-1",
        kind: "positions",
        link: "unlinked",
        position: { referencePanel: "accounts-1", direction: "within" },
      },
      // Inserted directly right of chart-1 (before Accounts): chart-1 | chart-2 | Accounts.
      {
        id: "chart-2",
        kind: "chart",
        link: "green",
        tf: "5m",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 560,
      },
    ],
  },
  {
    // SPEC §17.3 Scalper: Chart (1m) + Execution panel (DOM mode) + bottom dock.
    id: "scalper",
    name: "Scalper",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "execution-1",
        kind: "execution",
        link: "red",
        mode: "dom",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 340,
      },
      ...bottomDock("chart-1"),
    ],
  },
  {
    // Owner mockup 03: Daily Trading panel left, chart, order pad + tape right, bottom dock.
    id: "daily-trading",
    name: "Daily trading",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "journal-1",
        kind: "journal",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "left" },
        size: 300,
      },
      {
        id: "execution-1",
        kind: "execution",
        link: "red",
        mode: "dom",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 300,
      },
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "execution-1", direction: "below" },
        size: 220,
      },
      ...bottomDock("chart-1", 180),
    ],
  },
  {
    // Owner mockup 01: chart, Ladder, Time & sales, Watchlist, Positions below.
    id: "ladder",
    name: "Single instrument ladder",
    panels: [
      // Each is inserted directly right of the chart, so each takes its width from the chart
      // (not from its neighbour): final order chart | ladder | tape | watchlist.
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "watchlist-1",
        kind: "watchlist",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 300,
      },
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 210,
      },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 380,
      },
      ...bottomDock("chart-1", 220),
    ],
  },
  {
    id: "order-flow",
    name: "Order flow",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 220,
      },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "tape-1", direction: "right" },
        size: 380,
      },
      ...bottomDock("chart-1", 160),
    ],
  },
  {
    id: "prop-eval",
    name: "Prop evaluation",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 380,
      },
      {
        id: "risk-1",
        kind: "risk",
        link: "unlinked",
        position: { referencePanel: "dom-1", direction: "below" },
        size: 320,
      },
      ...bottomDock("chart-1", 160),
      {
        id: "journal-1",
        kind: "journal",
        link: "unlinked",
        position: { referencePanel: "positions-1", direction: "within" },
      },
    ],
  },
  {
    id: "multi-chart",
    name: "Multi-chart",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "chart-2",
        kind: "chart",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
      },
      {
        id: "chart-3",
        kind: "chart",
        link: "red",
        position: { referencePanel: "chart-1", direction: "below" },
      },
      {
        id: "chart-4",
        kind: "chart",
        link: "red",
        position: { referencePanel: "chart-2", direction: "below" },
      },
      {
        id: "scalp-1",
        kind: "scalp-pad",
        link: "red",
        mode: "scalp",
        position: { referencePanel: "chart-2", direction: "right" },
        size: 280,
      },
    ],
  },
  {
    // Owner 2026-09-27 "split chart view like TradingView / Tradesea": one chart module with a
    // 2×2 grid (the red symbol on 1m / 5m / 15m / 1h, crosshair synced) + order panel + dock.
    id: "chart-grid",
    name: "Chart grid 2×2",
    panels: [
      {
        id: "chart-1",
        kind: "chart",
        link: "red",
        params: {
          tf: "1m",
          chartLayout: {
            layout: "4-grid",
            panes: [
              { id: "p1", symbol: null, tf: "1m" },
              { id: "p2", symbol: "NQ", tf: "5m" },
              { id: "p3", symbol: "NQ", tf: "15m" },
              { id: "p4", symbol: "NQ", tf: "1h" },
            ],
            active: 0,
            ratios: {},
            sync: { symbol: true, interval: false, crosshair: true, time: false, dateRange: false },
          },
        },
      },
      {
        id: "execution-1",
        kind: "execution",
        link: "red",
        mode: "dom",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 320,
      },
      ...bottomDock("chart-1", 180),
    ],
  },
  {
    id: "replay",
    name: "Replay trainer",
    panels: [
      { id: "chart-1", kind: "chart", link: "red" },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 380,
      },
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "dom-1", direction: "right" },
        size: 220,
      },
      {
        id: "replay-1",
        kind: "replay",
        link: "red",
        position: { referencePanel: "chart-1", direction: "below" },
        size: 120,
      },
      {
        id: "journal-1",
        kind: "journal",
        link: "unlinked",
        position: { referencePanel: "replay-1", direction: "within" },
      },
    ],
  },
  {
    // Owner 2026-09-27, mockup 01 (Glacis, MMT.GG-based): chart with key levels, stats box and
    // position/order lines + tools rail | Ladder | Time & sales | Watchlist; Positions / Fills /
    // Journal under the chart only.
    id: "glacis-single",
    name: "Single instrument (Glacis)",
    panels: [
      {
        id: "chart-1",
        kind: "chart",
        link: "red",
        tf: "5m",
        rail: true,
        overlays: { levels: true, stats: true, trades: true, heatmap: false },
      },
      // Inserted directly right of the chart in reverse order: chart | ladder | tape | watchlist.
      {
        id: "watchlist-1",
        kind: "watchlist",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 300,
      },
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 180,
      },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 400,
      },
      {
        id: "positions-1",
        kind: "positions",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "below" },
        size: 380,
      },
      {
        id: "fills-1",
        kind: "fills",
        link: "unlinked",
        position: { referencePanel: "positions-1", direction: "within" },
      },
      {
        id: "journal-1",
        kind: "journal",
        link: "unlinked",
        position: { referencePanel: "positions-1", direction: "within" },
      },
    ],
  },
  {
    // Owner 2026-09-27, mockup 02 (Glacis): watchlist + futures screener | 1D and 4h charts | two
    // 15m charts with the liquidity heatmap | two Time & sales + trade ticket over Accounts |
    // Positions over Alerts. Red group = NQ, green group = ES (different symbols per row).
    id: "glacis-multi",
    name: "Multi-chart + heatmap (Glacis)",
    linkSymbols: { red: "NQ", green: "ES" },
    panels: [
      // The anchor states its width (1920 px screen: 240 + 300 + 600 + 520 + 260).
      { id: "chart-1", kind: "chart", link: "red", tf: "15m", overlays: ALL_OVERLAYS, size: 600 },
      {
        id: "positions-1",
        kind: "positions",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 260,
      },
      // The tape column: tape-1 seeds it (520 px), Accounts spans its foot, then tape-2 and the
      // ticket split the top cell — so tape-1's size is the column's and is not re-pinned.
      {
        id: "tape-1",
        kind: "tape",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 520,
        pin: false,
      },
      {
        id: "accounts-1",
        kind: "accounts",
        link: "unlinked",
        position: { referencePanel: "tape-1", direction: "below" },
        size: 170,
      },
      {
        id: "ticket-1",
        kind: "ticket",
        link: "red",
        mode: "order",
        position: { referencePanel: "tape-1", direction: "right" },
        size: 220,
      },
      {
        id: "tape-2",
        kind: "tape",
        link: "green",
        position: { referencePanel: "tape-1", direction: "right" },
        size: 150,
      },
      {
        id: "chart-2",
        kind: "chart",
        link: "red",
        tf: "1D",
        overlays: { trades: true, levels: false, stats: false, heatmap: false },
        position: { referencePanel: "chart-1", direction: "left" },
        size: 300,
      },
      {
        id: "watchlist-1",
        kind: "watchlist",
        link: "red",
        position: { referencePanel: "chart-2", direction: "left" },
        size: 240,
      },
      {
        id: "chart-4",
        kind: "chart",
        link: "green",
        tf: "15m",
        overlays: ALL_OVERLAYS,
        position: { referencePanel: "chart-1", direction: "below" },
        size: 460,
      },
      {
        id: "chart-3",
        kind: "chart",
        link: "green",
        tf: "4h",
        overlays: { trades: true, levels: true, stats: false, heatmap: false },
        position: { referencePanel: "chart-2", direction: "below" },
        size: 460,
      },
      {
        id: "screener-1",
        kind: "screener",
        link: "red",
        position: { referencePanel: "watchlist-1", direction: "below" },
        size: 560,
      },
      {
        id: "alerts-1",
        kind: "alerts",
        link: "red",
        position: { referencePanel: "positions-1", direction: "below" },
        size: 360,
      },
    ],
  },
  {
    id: "chart-focus",
    name: "Chart focus",
    linkSymbols: { red: "NQ" },
    panels: [
      {
        id: "chart-1",
        kind: "chart",
        link: "red",
        tf: "1m",
        params: { instrumentHeader: true, chartControlsCollapsed: true },
      },
      {
        id: "execution-1",
        kind: "execution",
        link: "red",
        mode: "dom",
        position: { referencePanel: "chart-1", direction: "within" },
        params: { domControls: { ladderOnly: true } },
      },
      {
        id: "positions-1",
        kind: "positions",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "within" },
      },
      {
        id: "orders-1",
        kind: "orders",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "within" },
      },
      {
        id: "accounts-1",
        kind: "accounts",
        link: "unlinked",
        position: { referencePanel: "chart-1", direction: "within" },
      },
    ],
  },
  {
    // Charting and order entry for a manually selected Rithmic practice leader. Copying is
    // configured externally in TradeCopia; this layout does not change the account or session.
    id: "paper-leader",
    name: "Rithmic practice leader",
    linkSymbols: { red: "NQ" },
    panels: [
      {
        id: "chart-1",
        kind: "chart",
        link: "red",
        tf: "1m",
        rail: true,
        params: { instrumentHeader: true },
      },
      {
        id: "dom-1",
        kind: "dom",
        link: "red",
        position: { referencePanel: "chart-1", direction: "right" },
        size: 340,
      },
      ...bottomDock("chart-1"),
    ],
  },
  // Start from nothing and build with "Add module".
  { id: "empty", name: "Empty", panels: [] },
];
